/**
 * Account / profile settings page (#152).
 *
 * STATE-DRIVEN. The page renders entirely from `Model.account`; an Effect
 * fetches the signed-in user over HTTP (`GET /auth/user`) and stores the
 * result in the Model, where the view reads it through `state.map(...)` /
 * `branch(...)`. There is NO imperative DOM here — no `document.create-
 * Element`, no `onMount` body-building, no document queries. (A prior
 * attempt failed precisely because it built the page imperatively in
 * `onMount`, which renders unreliably under the llui signal runtime.)
 *
 * Cards (30-design-system.md §9.3 typed-settings rhythm):
 *   - Profile  — email, account id, member-since, verification status.
 *   - Security — 2FA status + enable (setup → confirm → backup codes) /
 *                disable (email-confirm flow); "send password reset"
 *                button (password change is email-based — there is no
 *                in-place change-password endpoint).
 *   - Danger   — delete account, gated behind a typed confirmation.
 *
 * a11y: every control has a visible label + focus ring; status is never
 * color-only (a text token always accompanies the color); the destructive
 * action is clearly marked and gated. Every string flows through `t()`.
 */
import { button, card, pageHeader, textInput } from '@dl/ui';
import {
  branch,
  code,
  div,
  h2,
  label,
  li,
  p,
  show,
  span,
  text,
  ul,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import type { CurrentUser } from '../auth/api.js';
import { t } from '../i18n.js';

/** The in-progress 2FA enrolment (none until the GM clicks "Enable"). */
export interface AccountTwofaSetup {
  secretBase32: string;
  otpauthUri: string;
  /** The 6-digit code the GM types from their authenticator. */
  code: string;
  /** Backup codes returned by `/2fa/confirm` (shown once). `null` until confirmed. */
  backupCodes: string[] | null;
}

/**
 * The account-page slice of the Model. All JSON-serialisable Records —
 * no Maps / class instances (llui constraint). Loading + error are
 * explicit flags set by the load Effect so the view branches on them.
 */
export interface AccountModel {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  error: string | null;
  user: CurrentUser | null;
  twofaSetup: AccountTwofaSetup | null;
  /** Transient success/info banner (e.g. "reset link sent"). */
  notice: string | null;
  /** An action POST is in flight (disables the action buttons). */
  busy: boolean;
  /** The typed confirmation string for the delete-account gate. */
  deleteConfirm: string;
  /** #152 follow-up — the recovery-email editor buffer (seeded on load). */
  recoveryEmailInput: string;
  /**
   * #F8 re-authentication buffer for the recovery-email save. The recovery
   * mailbox is the password-reset redirect target, so the server re-verifies
   * the current password before repointing it — a hijacked session must not be
   * able to. Held only for the duration of one save and cleared as soon as the
   * request settles either way (see the `account/recovery-email-saved` and
   * `account/action-failed` arms), so a credential never lingers in Model.
   */
  recoveryPasswordInput: string;
}

export const INITIAL_ACCOUNT_MODEL: AccountModel = {
  status: 'idle',
  error: null,
  user: null,
  twofaSetup: null,
  notice: null,
  busy: false,
  deleteConfirm: '',
  recoveryEmailInput: '',
  recoveryPasswordInput: '',
};

/** Host contract — the Msgs the cards dispatch back into the TEA loop. */
export interface AccountMsgFns<Msg> {
  enableTwofa: () => Msg;
  twofaCodeInput: (code: string) => Msg;
  confirmTwofa: () => Msg;
  cancelTwofa: () => Msg;
  disableTwofa: () => Msg;
  sendPasswordReset: () => Msg;
  recoveryEmailInput: (value: string) => Msg;
  recoveryPasswordInput: (value: string) => Msg;
  saveRecoveryEmail: () => Msg;
  deleteConfirmInput: (value: string) => Msg;
  deleteAccount: () => Msg;
  dismissNotice: () => Msg;
}

const DELETE_CONFIRM_PHRASE = 'DELETE';

interface AccountViewSlice {
  account: AccountModel;
}

/** Locale-aware ISO date → display (just the date part). */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    const locale =
      typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d);
  } catch {
    return iso;
  }
}

// ─── Profile card ────────────────────────────────────────────────────────

function viewProfileCard<Slice extends AccountViewSlice>(
  state: Signal<Slice>,
): Mountable {
  const user = (s: Slice): CurrentUser | null => s.account.user;
  return card({
    padding: 'lg',
    className: 'account-section',
    testid: 'account-profile',
    children: [
      h2({ class: 'account-card-title' }, [text(t('account.profile.title'))]),
      div({ class: 'account-field-grid' }, [
        // Email
        div({ class: 'account-field' }, [
          span({ class: 'account-field-label' }, [text(t('account.profile.email'))]),
          span({ class: 'account-field-value', 'data-testid': 'account-email' }, [
            text(state.map((s) => user(s)?.email ?? '')),
          ]),
        ]),
        // Verification status — color + text token (color is never the only signal).
        div({ class: 'account-field' }, [
          span({ class: 'account-field-label' }, [text(t('account.profile.verification'))]),
          span(
            {
              class: state.map((s) =>
                user(s)?.email_verified_at
                  ? 'account-badge account-badge--ok'
                  : 'account-badge account-badge--warn',
              ),
              'data-testid': 'account-verification',
            },
            [
              text(
                state.map((s) =>
                  user(s)?.email_verified_at
                    ? t('account.profile.verified')
                    : t('account.profile.unverified'),
                ),
              ),
            ],
          ),
        ]),
        // Member since
        div({ class: 'account-field' }, [
          span({ class: 'account-field-label' }, [text(t('account.profile.member_since'))]),
          span({ class: 'account-field-value' }, [
            text(state.map((s) => formatDate(user(s)?.created_at ?? null))),
          ]),
        ]),
        // Account id (mono)
        div({ class: 'account-field' }, [
          span({ class: 'account-field-label' }, [text(t('account.profile.user_id'))]),
          code({ class: 'account-field-mono', 'data-testid': 'account-user-id' }, [
            text(state.map((s) => user(s)?.id ?? '')),
          ]),
        ]),
      ]),
    ],
  });
}

// ─── Security card ───────────────────────────────────────────────────────

function viewTwofaEnrolment<Slice extends AccountViewSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  fns: AccountMsgFns<Msg>,
): Mountable {
  // Two enrolment phases: collecting the code, or showing backup codes.
  return branch(
    state.map((s) => (s.account.twofaSetup?.backupCodes ? 'codes' : 'enter')),
    {
      enter: () => [
        div({ class: 'account-twofa-enrol', 'data-testid': 'account-twofa-enrol' }, [
          p({ class: 'account-help' }, [text(t('account.security.twofa.setup_help'))]),
          div({ class: 'account-twofa-secret' }, [
            span({ class: 'account-field-label' }, [text(t('account.security.twofa.secret'))]),
            code({ class: 'account-field-mono', 'data-testid': 'account-twofa-secret' }, [
              text(state.map((s) => s.account.twofaSetup?.secretBase32 ?? '')),
            ]),
          ]),
          label({ for: 'account-twofa-code', class: 'account-field-label' }, [
            text(t('account.security.twofa.code_label')),
          ]),
          textInput({
            type: 'text',
            testid: 'account-twofa-code',
            value: state.map((s) => s.account.twofaSetup?.code ?? ''),
            ariaLabel: t('account.security.twofa.code_label'),
            onInput: (value: string) => send(fns.twofaCodeInput(value)),
            attrs: {
              id: 'account-twofa-code',
              inputmode: 'numeric',
              autocomplete: 'one-time-code',
            },
          }),
          div({ class: 'account-actions' }, [
            button({
              variant: 'primary',
              className: 'account-button',
              testid: 'account-twofa-confirm',
              disabled: state.map((s) => s.account.busy),
              onClick: () => send(fns.confirmTwofa()),
              children: [text(t('account.security.twofa.confirm'))],
            }),
            button({
              variant: 'ghost',
              className: 'account-button',
              testid: 'account-twofa-cancel',
              onClick: () => send(fns.cancelTwofa()),
              children: [text(t('common.cancel'))],
            }),
          ]),
        ]),
      ],
      codes: () => [
        div({ class: 'account-twofa-codes', 'data-testid': 'account-twofa-codes' }, [
          p({ class: 'account-help account-help--strong' }, [
            text(t('account.security.twofa.backup_help')),
          ]),
          ul({ class: 'account-backup-codes', role: 'list' }, [
            ...Array.from({ length: 16 }).map((_unused, i) =>
              show(
                state.map((s) => (s.account.twofaSetup?.backupCodes?.[i] ?? '') !== ''),
                () => [
                  li({ class: 'account-backup-code' }, [
                    code([text(state.map((s) => s.account.twofaSetup?.backupCodes?.[i] ?? ''))]),
                  ]),
                ],
              ),
            ),
          ]),
          div({ class: 'account-actions' }, [
            button({
              variant: 'primary',
              className: 'account-button',
              testid: 'account-twofa-done',
              onClick: () => send(fns.cancelTwofa()),
              children: [text(t('account.security.twofa.done'))],
            }),
          ]),
        ]),
      ],
    },
  );
}

function viewSecurityCard<Slice extends AccountViewSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  fns: AccountMsgFns<Msg>,
): Mountable {
  const twofaOn = (s: Slice): boolean => s.account.user?.totp_enabled === true;
  const enrolling = (s: Slice): boolean => s.account.twofaSetup !== null;
  return card({
    padding: 'lg',
    className: 'account-section',
    testid: 'account-security',
    children: [
      h2({ class: 'account-card-title' }, [text(t('account.security.title'))]),
      // 2FA status row — color + text (never color-only).
      div({ class: 'account-field' }, [
        span({ class: 'account-field-label' }, [text(t('account.security.twofa.label'))]),
        span(
          {
            class: state.map((s) =>
              twofaOn(s) ? 'account-badge account-badge--ok' : 'account-badge account-badge--off',
            ),
            'data-testid': 'account-twofa-status',
          },
          [
            text(
              state.map((s) =>
                twofaOn(s) ? t('account.security.twofa.on') : t('account.security.twofa.off'),
              ),
            ),
          ],
        ),
      ]),
      p({ class: 'account-help' }, [text(t('account.security.twofa.help'))]),
      // Enable / disable controls — hidden while mid-enrolment (the
      // enrolment sub-view owns the buttons then).
      show(
        state.map((s) => !enrolling(s)),
        () => [
          div({ class: 'account-actions' }, [
            // Enable button — shown only when 2FA is off.
            show(
              state.map((s) => !twofaOn(s)),
              () => [
                button({
                  variant: 'primary',
                  className: 'account-button',
                  testid: 'account-twofa-enable',
                  disabled: state.map((s) => s.account.busy),
                  onClick: () => send(fns.enableTwofa()),
                  children: [text(t('account.security.twofa.enable'))],
                }),
              ],
            ),
            // Disable button — shown only when 2FA is on.
            show(
              state.map((s) => twofaOn(s)),
              () => [
                button({
                  variant: 'ghost',
                  className: 'account-button',
                  testid: 'account-twofa-disable',
                  disabled: state.map((s) => s.account.busy),
                  onClick: () => send(fns.disableTwofa()),
                  children: [text(t('account.security.twofa.disable'))],
                }),
              ],
            ),
          ]),
        ],
      ),
      // The enrolment flow (setup → confirm → backup codes).
      show(
        state.map((s) => enrolling(s)),
        () => [viewTwofaEnrolment(state, send, fns)],
      ),
      // Password — email-based reset (no in-place change endpoint).
      div({ class: 'account-subsection' }, [
        span({ class: 'account-field-label' }, [text(t('account.security.password.label'))]),
        p({ class: 'account-help' }, [text(t('account.security.password.help'))]),
        div({ class: 'account-actions' }, [
          button({
            variant: 'secondary',
            className: 'account-button',
            testid: 'account-password-reset',
            disabled: state.map((s) => s.account.busy),
            onClick: () => send(fns.sendPasswordReset()),
            children: [text(t('account.security.password.send_reset'))],
          }),
        ]),
      ]),
      // #152 follow-up — recovery email (moved off sign-up). A separate inbox
      // that password-reset (and admin reset/verification) emails route to when
      // set; blank to use the primary email. Editing it here is the only path.
      div({ class: 'account-subsection' }, [
        label({ for: 'account-recovery-email', class: 'account-field-label' }, [
          text(t('account.security.recovery.label')),
        ]),
        p({ class: 'account-help' }, [text(t('account.security.recovery.help'))]),
        textInput({
          type: 'email',
          testid: 'account-recovery-email',
          value: state.map((s) => s.account.recoveryEmailInput),
          ariaLabel: t('account.security.recovery.label'),
          onInput: (value: string) => send(fns.recoveryEmailInput(value)),
          attrs: {
            id: 'account-recovery-email',
            autocomplete: 'email',
            placeholder: t('account.security.recovery.placeholder'),
          },
        }),
        // #F8 — re-authentication. The server re-verifies the current password
        // before repointing the recovery mailbox (it is the password-reset
        // redirect target, so a hijacked session must not be able to change it).
        // An inline labelled field rather than a `window.prompt()`: a native
        // dialog is unreachable for assistive tech, unstyleable, untranslatable,
        // and blocks the automation the e2e gate depends on.
        label({ for: 'account-recovery-password', class: 'account-field-label' }, [
          text(t('account.security.recovery.password_label')),
        ]),
        p({ class: 'account-help' }, [text(t('account.security.recovery.password_help'))]),
        textInput({
          type: 'password',
          testid: 'account-recovery-password',
          value: state.map((s) => s.account.recoveryPasswordInput),
          ariaLabel: t('account.security.recovery.password_label'),
          onInput: (value: string) => send(fns.recoveryPasswordInput(value)),
          attrs: {
            id: 'account-recovery-password',
            autocomplete: 'current-password',
          },
        }),
        div({ class: 'account-actions' }, [
          button({
            variant: 'secondary',
            className: 'account-button',
            testid: 'account-recovery-save',
            disabled: state.map((s) => s.account.busy),
            onClick: () => send(fns.saveRecoveryEmail()),
            children: [text(t('account.security.recovery.save'))],
          }),
        ]),
      ]),
    ],
  });
}

// ─── Danger zone ───────────────────────────────────────────────────────────

function viewDangerCard<Slice extends AccountViewSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  fns: AccountMsgFns<Msg>,
): Mountable {
  const confirmed = (s: Slice): boolean => s.account.deleteConfirm.trim() === DELETE_CONFIRM_PHRASE;
  return card({
    variant: 'default',
    padding: 'lg',
    className: 'account-section account-danger',
    testid: 'account-danger',
    children: [
      h2({ class: 'account-card-title account-danger-title' }, [
        text(t('account.danger.title')),
      ]),
      p({ class: 'account-help' }, [text(t('account.danger.help'))]),
      label({ for: 'account-delete-confirm', class: 'account-field-label' }, [
        text(t('account.danger.confirm_label', { phrase: DELETE_CONFIRM_PHRASE })),
      ]),
      textInput({
        type: 'text',
        testid: 'account-delete-confirm',
        placeholder: DELETE_CONFIRM_PHRASE,
        value: state.map((s) => s.account.deleteConfirm),
        ariaLabel: t('account.danger.confirm_label', { phrase: DELETE_CONFIRM_PHRASE }),
        onInput: (value: string) => send(fns.deleteConfirmInput(value)),
        attrs: {
          id: 'account-delete-confirm',
          autocomplete: 'off',
        },
      }),
      div({ class: 'account-actions' }, [
        button({
          variant: 'danger',
          className: 'account-button',
          testid: 'account-delete',
          disabled: state.map((s) => !(confirmed(s) && !s.account.busy)),
          onClick: () => send(fns.deleteAccount()),
          children: [text(t('account.danger.delete'))],
        }),
      ]),
    ],
  });
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function viewAccount<Slice extends AccountViewSlice, Msg>(
  state: Signal<Slice>,
  send: Send<Msg>,
  fns: AccountMsgFns<Msg>,
): Mountable {
  return div({ class: 'account-page', 'data-testid': 'account-page', role: 'region' }, [
    pageHeader({ title: text(t('account.page.title')) }),
    // Transient notice banner (reset link sent, 2FA disable email sent, …).
    show(
      state.map((s) => s.account.notice !== null),
      () => [
        div(
          {
            class: 'account-notice',
            role: 'status',
            'aria-live': 'polite',
            'data-testid': 'account-notice',
          },
          [
            span([text(state.map((s) => s.account.notice ?? ''))]),
            button({
              variant: 'icon',
              className: 'account-notice-dismiss',
              ariaLabel: t('common.dismiss'),
              onClick: () => send(fns.dismissNotice()),
              children: [text('×')],
            }),
          ],
        ),
      ],
    ),
    // Load state machine: loading / error / loaded.
    branch(state.map((s) => s.account.status), {
      idle: () => [
        div({ class: 'account-loading', 'data-testid': 'account-loading', role: 'status' }, [
          text(t('account.loading')),
        ]),
      ],
      loading: () => [
        div({ class: 'account-loading', 'data-testid': 'account-loading', role: 'status' }, [
          text(t('account.loading')),
        ]),
      ],
      error: () => [
        div({ class: 'account-error', 'data-testid': 'account-error', role: 'alert' }, [
          text(state.map((s) => s.account.error ?? t('account.error'))),
        ]),
      ],
      loaded: () => [
        div({ class: 'account-cards' }, [
          viewProfileCard(state),
          viewSecurityCard(state, send, fns),
          viewDangerCard(state, send, fns),
        ]),
      ],
    }),
  ]);
}
